import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Plus, Trash2, Save, AlertCircle } from 'lucide-react';

const CURRENCY_OPTIONS = ['EUR', 'USD', 'XAF', 'GBP', 'CAD'];

const MerchandiseCategoryManager = ({ token }) => {
  const { t } = useLanguage();
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/company/settings');
      setSettings(res.data);
      setCategories(res.data.financial?.merchandise_categories || []);
    } catch {
      setError(t('cannotLoadCategories'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const addCategory = () => {
    setCategories([...categories, {
      id: crypto.randomUUID ? crypto.randomUUID() : `cat-${Date.now()}`,
      name: '',
      fixed_price: 0,
      currency: 'EUR',
      is_active: true,
    }]);
  };

  const updateCategory = (index, field, value) => {
    const updated = [...categories];
    updated[index] = { ...updated[index], [field]: value };
    setCategories(updated);
  };

  const removeCategory = (index) => {
    setCategories(categories.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        financial: {
          ...settings.financial,
          merchandise_categories: categories.map(c => ({
            ...c,
            fixed_price: parseFloat(c.fixed_price) || 0,
          })),
        },
      };
      await api.put('/api/company/settings', payload);
      setSuccess(t('categoriesSaved'));
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError(t('saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground py-4">{t('loading')}</div>;
  }

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{t('merchandiseCategories')}</CardTitle>
          <Button size="sm" variant="outline" onClick={addCategory}>
            <Plus size={14} className="mr-1" />
            {t('add')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="flex items-center gap-2 p-3 mb-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            <AlertCircle size={16} />
            {error}
          </div>
        )}
        {success && (
          <div className="p-3 mb-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm">
            {success}
          </div>
        )}

        {categories.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            {t('noCategoriesConfigured')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted border-b border-border">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">{t('name')}</th>
                  <th className="text-right px-3 py-2 font-medium text-muted-foreground">{t('fixedPrice')}</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">{t('currency')}</th>
                  <th className="text-center px-3 py-2 font-medium text-muted-foreground">{t('active')}</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {categories.map((cat, idx) => (
                  <tr key={cat.id || idx} className="hover:bg-muted">
                    <td className="px-3 py-2">
                      <Input
                        value={cat.name}
                        onChange={(e) => updateCategory(idx, 'name', e.target.value)}
                        placeholder={t('categoryNamePlaceholder')}
                        className="text-sm"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={cat.fixed_price}
                        onChange={(e) => updateCategory(idx, 'fixed_price', e.target.value)}
                        className="text-sm text-right w-24"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={cat.currency}
                        onChange={(e) => updateCategory(idx, 'currency', e.target.value)}
                        className="border border-border rounded px-2 py-1 text-sm"
                      >
                        {CURRENCY_OPTIONS.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={cat.is_active}
                        onChange={(e) => updateCategory(idx, 'is_active', e.target.checked)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => removeCategory(idx)}>
                        <Trash2 size={14} className="text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {categories.length > 0 && (
          <div className="flex justify-end mt-4">
            <Button onClick={handleSave} disabled={saving} size="sm">
              <Save size={14} className="mr-1" />
              {saving ? t('savingInProgress') : t('saveBtn')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MerchandiseCategoryManager;
